/* eslint-disable @typescript-eslint/no-unused-vars */
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import type { ZegoRoomStateChangedReason } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.rtm';
import type { ZegoSoundLevelInfo } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';
import { ZIMCallingInviteConfig, ZIMCallJoinConfig, ZIMPushConfig } from 'zego-zim-web';

import { BroadcastService } from '../../../services/broadcast.service';
import { HomieService } from '../../../services/homie.service';
import { LocalMediaPermissionService } from '../../../services/local-media-permission.service';
import { LocalNotificationService } from '../../../services/notification.service';
import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { ZimService } from '../../../services/zim.service';
import { createLogger } from '../../../utils/logger.util';
import { parseUrlEncodedToObject, safeJsonParse } from '../../../utils/utils';
import {
  CALL_ACTION,
  CALL_END_REASON,
  CALL_STATE,
  CALL_TYPE,
  RINGTONE_TYPE,
  ZegoQualityGrade,
} from '../constants';
import {
  CallActionData,
  CallEndReason,
  CallInfo,
  CallInvitation,
  CallParticipant,
  CallRingtoneType,
  CallType,
  PayloadDict,
  SwitchCallExtendedData,
} from '../types/call';
import { CallAudioService } from './call-audio.service';
import { CallLifecycleService } from './call-lifecycle.service';
import { CallMediaStore } from './call-media-store.service';
import { CallNetworkStoreService } from './call-network-store.service';
import { CallSessionStoreService } from './call-session-store.service';
import { CallZegoService } from './call-zego.service';
import { CallZegoListenerService } from './call-zego-listener.service';
import { CallZimService } from './call-zim.service';
import { CallZimEventHandlerService } from './call-zim-event-handler.service';

@Injectable({
  providedIn: 'root',
})
export class CallStoreService {
  private readonly logger = createLogger('Call - CallStoreService');

  public callZimService = inject(CallZimService);
  public zimService = inject(ZimService);
  public callZegoService = inject(CallZegoService);
  public localMediaPermissionService = inject(LocalMediaPermissionService);
  public zegoMediaControlService = inject(ZegoMediaControlService);
  public callAudio = inject(CallAudioService);
  public callSessionStore = inject(CallSessionStoreService);
  public callNetworkStore = inject(CallNetworkStoreService);
  public callZegoListenerService = inject(CallZegoListenerService);
  private callLifecycleService = inject(CallLifecycleService);
  private callZimEventHandler = inject(CallZimEventHandlerService);
  private callMediaStore = inject(CallMediaStore);
  public broadcastService = inject(BroadcastService);
  private toastr = inject(ToastrService);
  private readonly localNotificationService = inject(LocalNotificationService);
  private readonly homieService = inject(HomieService);

  // Signals for state management
  isModalHidden = signal<boolean>(false);
  private listenersInitialized = false;
  private windowListenersInitialized = false;
  private ringingConnectingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private outgoingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  currentCall = this.callSessionStore.currentCall;
  callState = this.callSessionStore.callState;
  callType = this.callSessionStore.callType;
  callEndReason = this.callSessionStore.callEndReason;
  hasCurrentCall = this.callSessionStore.hasCurrentCall;
  participants = this.callSessionStore.participants;
  pendingInvitation = this.callSessionStore.pendingInvitation;
  callStartTime = this.callSessionStore.callStartTime;
  callDurationText = this.callSessionStore.callDurationText;

  isMeClickAccept = this.callSessionStore.isMeClickAccept;
  isMeClickCall = this.callSessionStore.isMeClickCall;
  isLoginRoom = this.callSessionStore.isLoginRoom;

  isInCall = this.callSessionStore.isInCall;
  activeParticipants = this.callSessionStore.activeParticipants;
  hasPendingCall = this.callSessionStore.hasPendingCall;
  hasCalleeIncomingCall = this.callSessionStore.hasCalleeIncomingCall;
  showCallStatusBar = this.callSessionStore.showCallStatusBar;
  isInviter = this.callSessionStore.isInviter;
  pendingParticipants = this.callSessionStore.pendingParticipants;

  networkQualityInfo = this.callNetworkStore.networkQualityInfo;
  isShowMyNetworkWarning = this.callNetworkStore.isShowMyNetworkWarning;
  isShowMyNetworkReconnecting = this.callNetworkStore.isShowMyNetworkReconnecting;
  isShowRemoteNetworkWarning = this.callNetworkStore.isShowRemoteNetworkWarning;

  remoteSoundLevels = this.callSessionStore.remoteSoundLevels;

  isMicPermissionGranted = this.callMediaStore.isMicPermissionGranted;
  isCamPermissionGranted = this.callMediaStore.isCamPermissionGranted;
  hasCamDevice = this.callMediaStore.hasCamDevice;
  hasMicDevice = this.callMediaStore.hasMicDevice;

  // Computed signals
  desiredRingtone = computed<CallRingtoneType | null>(() => {
    const state = this.callState();
    const pending = this.pendingInvitation();
    const currentCall = this.currentCall();

    if (state === CALL_STATE.CONNECTING || state === CALL_STATE.CONNECTED) return null;

    const isCaller = !!currentCall && currentCall.inviter === this.zimService.userId;
    const isCallee = !isCaller && !!pending;
    if (!isCaller && !isCallee) return null;

    if (this.hasCalleeIncomingCall()) return RINGTONE_TYPE.CALLEE_INCOMING;
    if (state === CALL_STATE.RINGING) return RINGTONE_TYPE.RINGING;
    if (state === CALL_STATE.OUTGOING) return RINGTONE_TYPE.OUTGOING;
    return null;
  });

  hasCamAndPermission = this.callMediaStore.hasCamAndPermission;
  hasMicAndPermission = this.callMediaStore.hasMicAndPermission;
  localStream = this.callMediaStore.localStream;
  localStreamId = this.callMediaStore.localStreamId;
  localMicState = this.callMediaStore.localMicState;
  localCamState = this.callMediaStore.localCamState;
  localSoundLevel = this.callMediaStore.localSoundLevel;
  isScreenSharing = this.callMediaStore.isScreenSharing;
  isRemoteSharing = this.callMediaStore.isRemoteSharing;
  isAnyScreenSharing = this.callMediaStore.isAnyScreenSharing;

  readonly networkQualityInfoName = computed(() => {
    const info = this.networkQualityInfo();
    const isMyNetworkWeak = this.isShowMyNetworkWarning() || this.isShowMyNetworkReconnecting();
    const isRemoteNetworkWeak = this.isShowRemoteNetworkWarning();

    if (isMyNetworkWeak) return 'bạn';
    if (isRemoteNetworkWeak) {
      const problematicRemote = info.remoteState.find(
        (remote) =>
          remote.upstreamQuality === ZegoQualityGrade.Poor ||
          remote.downstreamQuality === ZegoQualityGrade.Poor ||
          remote.upstreamQuality === ZegoQualityGrade.Die ||
          remote.downstreamQuality === ZegoQualityGrade.Die,
      );
      const participant = this.participants().find((p) => p.userId === problematicRemote?.userId);
      return participant?.userName || 'đối phương';
    }
    return '';
  });

  constructor() {
    this.callNetworkStore.setNetworkTimeoutHandler(() =>
      this.forceEndCall(CALL_END_REASON.NETWORK, 'end'),
    );

    this.broadcastService.call$.subscribe(({ action, payload }: CallActionData) => {
      switch (action) {
        case CALL_ACTION.ACCEPT_CALL: {
          this.callAudio.stop();
          const { callId } = payload;

          if (this.pendingInvitation()?.callId === callId) {
            const currentCall = this.currentCall();

            // Trường hợp đang có cuộc gọi đến và accept trên tab khác hoặc app khác
            if (currentCall && currentCall.callId !== callId) {
              const request =
                currentCall.inviter === this.zimService.userId
                  ? this.callZimService.callEnd(currentCall.callId, {
                      extendedData: '',
                    })
                  : this.callZimService.callQuit(currentCall.callId, {
                      extendedData: '',
                    });

              Promise.resolve(request).catch((error) => {
                this.logger.warn('Failed to close current call', error);
              });

              if (this.isLoginRoom()) {
                try {
                  this.callZegoService.logoutRoom(currentCall.callId);
                } catch (_err) {
                  this.logger.warn('logoutRoom error on accept:', _err);
                }
              }
              const localStream = this.localStream();
              const localStreamId = this.localStreamId();

              if (localStream && localStreamId) {
                try {
                  this.callZegoService.stopPubishAndDestroyStream(localStream, localStreamId);
                } catch (_err) {
                  this.logger.warn('stopPubishAndDestroyStream error on accept:', _err);
                }
              }
              if (this.zegoMediaControlService.mediaState().isScreenSharing) {
                try {
                  this.zegoMediaControlService.stopScreenShare();
                } catch (_err) {
                  this.logger.warn('stopScreenShare error on accept:', _err);
                }
              }
            }

            this.reset();
          }
          return;
        }
        case CALL_ACTION.REJECT_CALL: {
          const { callId } = payload;

          if (this.pendingInvitation()?.callId === callId) {
            this.callSessionStore.setPendingInvitation(null);

            if (this.callState() !== CALL_STATE.CONNECTED) {
              this.callSessionStore.setCurrentCallState(CALL_STATE.IDLE);
            }
          }
          return;
        }
      }
    });

    effect(() => {
      const state = this.callState();

      if (
        state !== CALL_STATE.RINGING &&
        state !== CALL_STATE.CONNECTING &&
        this.ringingConnectingTimeoutId
      ) {
        clearTimeout(this.ringingConnectingTimeoutId);
        this.ringingConnectingTimeoutId = null;
      }
      if (state !== CALL_STATE.OUTGOING && this.outgoingTimeoutId) {
        clearTimeout(this.outgoingTimeoutId);
        this.outgoingTimeoutId = null;
      }

      if (
        (state === CALL_STATE.RINGING || state === CALL_STATE.CONNECTING) &&
        !this.ringingConnectingTimeoutId
      ) {
        const call = this.currentCall();
        let duration = 40000;

        if (call?.isSwitchCall) {
          duration = 30000;
        } else if (call && call.startTime) {
          const expiresAt = call.startTime.getTime() + 60000;
          const remaining = expiresAt - Date.now();
          if (remaining <= 0) {
            return;
          }
          duration = remaining;
        }

        this.ringingConnectingTimeoutId = setTimeout(() => {
          this.forceEndCall(
            state === CALL_STATE.RINGING ? CALL_END_REASON.NO_ANSWER : CALL_END_REASON.TIMEOUT,
            state === CALL_STATE.RINGING ? 'cancel' : 'end',
          );
        }, duration);
      } else if (state === CALL_STATE.OUTGOING && !this.outgoingTimeoutId) {
        this.outgoingTimeoutId = setTimeout(() => {
          this.forceEndCall(CALL_END_REASON.TIMEOUT, 'cancel');
        }, 30000);
      }
    });

    effect(() => {
      const pending = this.pendingInvitation();
      if (!pending && this.pendingTimeoutId) {
        clearTimeout(this.pendingTimeoutId);
        this.pendingTimeoutId = null;
      } else if (pending && !this.pendingTimeoutId) {
        const expiresAt = pending.receivedAt.getTime() + pending.timeoutSeconds * 1000;
        const remaining = expiresAt - Date.now();
        if (remaining > 0) {
          console.log('[DEBUG-CALL]', 'manual time out set timeout with', remaining);
          this.pendingTimeoutId = setTimeout(() => {
            console.log('[DEBUG-CALL]', 'manual time out');
            this.showMissingCallNotification();
            this.handleCallInvitationError(pending.callId, CALL_END_REASON.TIMEOUT);
          }, remaining);
        } else {
          console.log('[DEBUG-CALL]', 'manual time out 2');
          this.showMissingCallNotification();
          this.handleCallInvitationError(pending.callId, CALL_END_REASON.TIMEOUT);
        }
      }
    });

    effect(() => {
      const state = this.callState();
      const pending = this.pendingInvitation();
      const currentCall = this.currentCall();
      const isCaller = !!currentCall && currentCall.inviter === this.zimService.userId;
      const isCallee = !isCaller && !!pending;

      if (state === CALL_STATE.RINGING && !isCaller && !isCallee) {
        this.logger.warn(
          'Invalid ringing state detected - not caller and no pending invitation. Clearing state.',
        );
        this.callSessionStore.setCurrentCallState(CALL_STATE.IDLE);
      }
    });

    effect(
      () => {
        this.syncRingtoneFromState();
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(() => {
      if (this.zimService.isLoginZim() === true) {
        if (!this.listenersInitialized) {
          this.callZimEventHandler.setupEventListeners();
          this.listenersInitialized = true;
          this.logger.log('Event listeners initialized once');
        }
        if (!this.windowListenersInitialized) {
          window.addEventListener('offline', () => {
            this.forceLocalNetworkDie('browser offline');
          });

          window.addEventListener('online', () => {
            this.onLocalReconnected();
          });
          this.windowListenersInitialized = true;
        }
      }
    });

    effect(() => {
      const currentCall = this.currentCall();
      if (currentCall) {
        this.callZegoListenerService.register({
          onRemoteMicStatusUpdate: this.handleRemoteMicStatusUpdate.bind(this),
          onRemoteCameraStatusUpdate: this.handleRemoteCameraStatusUpdate.bind(this),
          onSoundLevelUpdate: this.handleSoundLevelUpdate.bind(this),
          onNetworkQuality: this.handleNetworkQuality.bind(this),
          onRoomStateChanged: this.handleRoomStateChanged.bind(this),
          onPublisherStateUpdate: (result) => {
            this.logger.log('Publisher State Update:', result);
          },
        });
      }
    });

    effect(() => {
      const callId = this.currentCall()?.callId;
      if (!callId || this.callState() !== CALL_STATE.CONNECTED) return;
      untracked(() => this.homieService.markCallConnected(callId));
    });

    this.callZegoService.setStreamDeleteTimeoutHandler(() =>
      this.forceEndCall(CALL_END_REASON.NETWORK, 'end'),
    );

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handleCloseTab);
      window.addEventListener('beforeunload', this.handleCloseTab);
    }
  }

  private handleCloseTab = (ev: PageTransitionEvent | BeforeUnloadEvent) => {
    const currentCall = this.currentCall();
    const pendingInvitation = this.pendingInvitation();
    const callState = this.callState();

    if (callState === CALL_STATE.ENDED && !pendingInvitation && !currentCall) return;

    // Trường hợp 4: nếu đã accept mà có cuộc gọi thứ 3 thì gọi callReject cuộc gọi đến
    // và người nhận/người gọi gọi callEnd/callQuit cho cuộc gọi đã accept
    if (currentCall && pendingInvitation && currentCall.callId !== pendingInvitation.callId) {
      try {
        this.callZimService.callReject(pendingInvitation.callId, { extendedData: '' });
      } catch (error) {
        this.logger.warn('handleCloseTab callReject error:', error);
      }

      if (currentCall.inviter === this.zimService.userId) {
        try {
          this.callZimService.callEnd(currentCall.callId, { extendedData: '' });
        } catch (error) {
          this.logger.warn('handleCloseTab callEnd error:', error);
        }
      } else {
        try {
          this.callZimService.callQuit(currentCall.callId, { extendedData: '' });
        } catch (error) {
          this.logger.warn('handleCloseTab callQuit error:', error);
        }
      }
      return;
    }

    // Nếu chỉ có pendingInvitation mà chưa accept (trường hợp 2)
    // Trường hợp 2 (fallback): nếu là người nhận chưa accept thì gọi callReject
    if (!currentCall && pendingInvitation) {
      try {
        this.callZimService.callReject(pendingInvitation.callId, { extendedData: '' });
      } catch (error) {
        this.logger.warn('handleCloseTab callReject error:', error);
      }
      return;
    }

    if (currentCall) {
      if (callState === CALL_STATE.OUTGOING || callState === CALL_STATE.RINGING) {
        // Trường hợp 1: nếu là người gọi chưa accept thì gọi callCancel
        try {
          const invitees = this.participants().map((p) => p.userId);
          this.callZimService.callCancel(invitees, currentCall.callId, { extendedData: '' });
        } catch (error) {
          this.logger.warn('handleCloseTab callCancel error:', error);
        }
      } else if (callState === CALL_STATE.CONNECTED || callState === CALL_STATE.CONNECTING) {
        // Trường hợp 3: nếu đã accept, người gọi gọi callEnd, người nhận gọi callQuit
        if (currentCall.inviter === this.zimService.userId) {
          try {
            this.callZimService.callEnd(currentCall.callId, { extendedData: '' });
          } catch (error) {
            this.logger.warn('handleCloseTab callEnd error:', error);
          }
        } else {
          try {
            this.callZimService.callQuit(currentCall.callId, { extendedData: '' });
          } catch (error) {
            this.logger.warn('handleCloseTab callQuit error:', error);
          }
        }
      }
    }
  };

  // Actions
  async callInvite(
    invitees: string[],
    type: CallType = CALL_TYPE.AUDIO,
    extendedData = '',
  ): Promise<void> {
    return this.callLifecycleService.callInvite(invitees, type, extendedData);
  }

  async callAccept(callId: string): Promise<void> {
    this.localNotificationService.clearCallAlert(callId);
    return this.callLifecycleService.callAccept(callId);
  }

  async acceptIncomingCall() {
    this.callLifecycleService.acceptIncomingCall();
  }

  async switchCall() {
    return this.callLifecycleService.switchCall();
  }

  async callReject(callId: string, isRejectSwitchCall = false): Promise<void> {
    console.log('[DEBUG-CALL]', 'callReject', JSON.stringify({ callId, isRejectSwitchCall }));
    this.localNotificationService.clearCallAlert(callId);
    return this.callLifecycleService.callReject(callId, isRejectSwitchCall);
  }

  async rejectBusy(type: CallType, callID: string) {
    const busyRejectExtendedData = {
      type,
      callID,
      reason: 'busy_pre_room_calling',
      endReason: 'BUSY',
      system: 'Web',
    };
    try {
      await this.callZimService.callReject(callID, {
        extendedData: JSON.stringify(busyRejectExtendedData),
      });
      console.log('[DEBUG-CALL]', 'rejectBusy success');
    } catch (error) {
      this.logger.warn('rejectBusy error:', error);
      console.log('[DEBUG-CALL]', 'rejectBusy error', JSON.stringify(error));
    }
  }

  async callEnd(): Promise<void> {
    return this.callLifecycleService.callEnd();
  }

  // Note
  async joinCall(callId: string): Promise<void> {
    try {
      const config: ZIMCallJoinConfig = {
        extendedData: '',
      };
      await this.callZimService.callJoin(callId, config);
      this.callSessionStore.setCurrentCallState(CALL_STATE.CONNECTED);
    } catch (error) {
      this.logger.error('Failed to join call:', error);
      throw error;
    }
  }

  async callQuit(callId: string): Promise<void> {
    return this.callLifecycleService.callQuit(callId);
  }

  async callCancel(invitees: string[]): Promise<void> {
    return this.callLifecycleService.callCancel(invitees);
  }

  async endCurrentCall(): Promise<void> {
    const state = this.callState();

    if (state === CALL_STATE.OUTGOING || state === CALL_STATE.RINGING) {
      const call = this.currentCall();
      if (call) {
        const invitees = this.participants()
          .filter((p) => p.userId !== this.zimService.userId)
          .map((p) => p.userId);
        if (invitees.length > 0) {
          await this.callCancel(invitees);
        }
      }
      return;
    }

    if (state === CALL_STATE.CONNECTED || state === CALL_STATE.CONNECTING) {
      if (this.currentCall()?.inviter === this.zimService.userId) {
        await this.callEnd();
      } else if (this.currentCall()) {
        await this.callQuit(this.currentCall()!.callId);
      }
    }
  }

  async callingInvite(invitees: string[], callId: string, payload: string): Promise<void> {
    try {
      const pushConfig: ZIMPushConfig = {
        resourcesID: 'appviettel_call',
        title: `Cuộc gọi đến từ ${this.zimService.userId ?? ''}`,
        content: 'Push Notification',
        enableBadge: true,
        badgeIncrement: 1,
        payload: payload,
        threadID: '',
      };

      const config: ZIMCallingInviteConfig = {
        pushConfig,
      };

      await this.callZimService.callingInvite(invitees, callId, config);
    } catch (error) {
      this.logger.warn('Failed to callingInvite:', error);
      throw error;
    }
  }

  resolveTimeoutEndReason(): CallEndReason {
    return this.callState() === CALL_STATE.RINGING
      ? CALL_END_REASON.NO_ANSWER
      : CALL_END_REASON.TIMEOUT;
  }

  async handleCallInvitationError(callID: string, endReason: CallEndReason) {
    if (this.callState() === CALL_STATE.ENDED) return;

    const currentCall = this.currentCall();
    const pendingInvitation = this.pendingInvitation();
    const isCurrentCall = currentCall?.callId === callID;
    const isPendingCall = pendingInvitation?.callId === callID;

    if (isPendingCall && this.pendingTimeoutId) {
      clearTimeout(this.pendingTimeoutId);
      this.pendingTimeoutId = null;
    }

    // Nếu cuộc gọi thứ 3 (pending call) bị kết thúc/hủy/timeout khi đang có cuộc gọi hiện tại
    if (isPendingCall && !isCurrentCall && currentCall) {
      const resetReasons: CallEndReason[] = [
        CALL_END_REASON.TIMEOUT,
        CALL_END_REASON.NO_ANSWER,
        CALL_END_REASON.CANCELLED,
        CALL_END_REASON.ENDED,
        CALL_END_REASON.NORMAL,
        CALL_END_REASON.REJECTED,
      ];
      if (resetReasons.includes(endReason)) {
        this.callSessionStore.setPendingInvitation(null);
        return;
      }
    }

    const shouldPlayEndAudio = isCurrentCall || (isPendingCall && !currentCall);
    if (shouldPlayEndAudio) {
      try {
        this.callAudio.stop();
        this.callAudio.play(RINGTONE_TYPE.END_CALL);
      } catch (error) {
        this.logger.warn('Failed to play end call audio:', error);
      }
    }

    // nếu còn cuộc gọi chờ (pending khác callID đang end) thì giữ lại,
    // chỉ xóa pending khi chính nó là cuộc gọi đang end.
    const pendingToPreserve =
      pendingInvitation && pendingInvitation.callId !== callID ? pendingInvitation : null;
    if (isPendingCall) {
      this.callSessionStore.setPendingInvitation(null);
    }
    if (!currentCall) return;

    // nếu không phải cuộc gọi hiện tại thì không set error
    if (!isCurrentCall) return;

    if (this.localStream()) {
      try {
        this.callZegoService.stopPubishAndDestroyStream(this.localStream(), this.localStreamId()!);
      } catch (err) {
        this.logger.warn('stopPubishAndDestroyStream error:', err);
      }
    }

    if (this.zegoMediaControlService.mediaState().isScreenSharing) {
      try {
        this.zegoMediaControlService.stopScreenShare();
      } catch (err) {
        this.logger.warn('stopScreenShare error:', err);
      }
    }

    if (this.isLoginRoom()) {
      try {
        this.callZegoService.logoutRoom(currentCall.callId);
        if (endReason === CALL_END_REASON.BUSY) {
          const invitees = this.participants().map((p) => p.userId);
          await this.callZimService.callCancel(invitees, currentCall.callId, {
            extendedData: '',
          });
        }
      } catch (err) {
        this.logger.warn('logoutRoom error:', err);
      }
      this.callSessionStore.isLoginRoom.set(false);
    }

    // đã accept || khi mình là người nhận
    if (
      endReason === CALL_END_REASON.ENDED ||
      endReason === CALL_END_REASON.NORMAL ||
      currentCall.inviter !== this.zimService.userId
    ) {
      // this.callSessionStore.setCurrentCallState(CALL_STATE.ENDED);
      // this.callSessionStore.updateCurrentCall((call) =>
      //   call ? { ...call, endTime: new Date(), endReason } : null,
      // );
      // setTimeout(() => {
      // giữ cuộc gọi chờ ở trạng thái WAITING, không đóng modal.
      this.reset(!!pendingToPreserve);
      // }, 1500);
      return;
    }

    // người gọi cancel call && chưa trong cuộc gọi
    if (
      endReason === CALL_END_REASON.CANCELLED &&
      (this.callState() === CALL_STATE.OUTGOING || this.callState() === CALL_STATE.RINGING)
    ) {
      // giữ cuộc gọi chờ ở trạng thái WAITING, không đóng modal.
      this.reset(!!pendingToPreserve);
      return;
    }
    // nếu còn pending chờ thì về IDLE và giữ WAITING thay vì ENDED
    // để tránh auto-close modal 5s xóa luôn cuộc gọi đến.
    if (pendingToPreserve) {
      this.reset(true);
      return;
    }
    // thay đổi trạng thái
    this.callSessionStore.setCurrentCallState(CALL_STATE.ENDED);
    this.callSessionStore.updateCurrentCall((call) =>
      call ? { ...call, endTime: new Date(), endReason } : null,
    );
  }

  isSwitchCall(extendedData: string): boolean {
    const switchCallExtendedData = safeJsonParse<SwitchCallExtendedData>(extendedData);
    if (
      switchCallExtendedData?.reason === 'switch-call' &&
      switchCallExtendedData.userId === this.zimService.userId
    ) {
      return true;
    }
    return false;
  }

  //#region Handle remote media state
  handleRemoteMicStatusUpdate(streamID: string, status: 'OPEN' | 'MUTE'): void {
    this.callMediaStore.handleRemoteMicStatusUpdate(streamID, status);
  }

  handleRemoteCameraStatusUpdate(streamID: string, status: 'OPEN' | 'MUTE'): void {
    this.callMediaStore.handleRemoteCameraStatusUpdate(streamID, status);
  }

  handleSoundLevelUpdate(soundLevelList: ZegoSoundLevelInfo[]): void {
    this.callMediaStore.handleSoundLevelUpdate(soundLevelList);
  }

  handleRoomStateChanged(
    roomID: string,
    reason: ZegoRoomStateChangedReason,
    errorCode: number,
    extendedData: string,
  ) {
    this.logger.log('Room State Changed:', { roomID, reason, errorCode });
    switch (reason) {
      case 'KICKOUT':
        {
          const extendDataTemp = parseUrlEncodedToObject(extendedData ?? '');
          const message = safeJsonParse(extendDataTemp);
          if (
            message &&
            message.custom_kickout_message &&
            message.custom_kickout_message ===
              'kickout may be the same user ID login other; kickout reason: 1'
          ) {
            if (!this.currentCall()) return;
            const action =
              this.callState() === CALL_STATE.CONNECTED || this.callState() === CALL_STATE.CONNECTED
                ? 'end'
                : 'cancel';
            this.forceEndCall(CALL_END_REASON.NORMAL, action);
            this.toastr.error('Cuộc gọi đang được thực hiện trên thiết bị khác.');
          }
        }
        break;
    }
  }

  async publishCallStream(callId: string, type: CallType) {
    return this.callMediaStore.publishCallStream(callId, type);
  }

  handleNetworkQuality(userID: string, upstreamQuality: number, downstreamQuality: number) {
    this.callNetworkStore.handleNetworkQuality(
      userID,
      upstreamQuality,
      downstreamQuality,
      this.isLoginRoom(),
    );
  }

  private forceEndCall(reason: CallEndReason, action: 'cancel' | 'end') {
    const call = this.currentCall();
    if (!call) return;

    this.handleCallInvitationError(call.callId, reason);

    try {
      if (action === 'cancel') {
        if (call.inviter === this.zimService.userId) {
          const invitees = this.participants().map((p) => p.userId);
          this.callZimService.callCancel(invitees, call.callId, { extendedData: '' });
        }
      } else if (action === 'end') {
        if (call.inviter === this.zimService.userId) {
          this.callZimService.callEnd(call.callId, { extendedData: '' });
        } else {
          this.callZimService.callQuit(call.callId, { extendedData: '' });
        }
      }
    } catch (error) {
      this.logger.warn('forceEndCall error:', error);
    }
  }

  private forceLocalNetworkDie(reason: string) {
    this.logger.log('Force local Die:', reason);
    this.callNetworkStore.forceLocalNetworkDie();
  }

  private onLocalReconnected() {
    this.logger.log('Local reconnected - waiting for real quality');
    this.callNetworkStore.onLocalReconnected();
  }

  switchCallType() {
    this.callSessionStore.updateCurrentCall((call) =>
      call ? { ...call, type: CALL_TYPE.VIDEO } : null,
    );
  }

  private syncRingtoneFromState(): void {
    const ringtone = this.desiredRingtone();
    if (!ringtone) {
      this.callAudio.stop();
      return;
    }
    // Đảm bảo ringtone đã được khởi tạo trước khi phát
    this.callAudio.initAudio();
    this.callAudio.play(ringtone);
  }

  createNewInvitation(
    createTo: 'Received' | 'Created',
    callId: string,
    participants: CallParticipant[],
    extendedData: PayloadDict,
    invitation?: CallInvitation,
  ) {
    this.isModalHidden.set(false);
    // Có cuộc gọi đến hoặc mình gọi đi -> khởi tạo ringtone
    this.callAudio.initAudio();
    if (createTo === 'Received' && invitation) {
      this.callSessionStore.setPendingInvitation(invitation);

      if (document.hidden) {
        const callID = invitation.callId;
        const callerID = invitation.inviter;
        const callerName = extendedData.callerName || callId;
        const callerAvatar = participants.find((el) => el.userId === callerID)?.avatar || '';

        console.log('DEBUG-CALL', 'Cuộc gọi đến', 'triggerCallAlert', callID);
        this.localNotificationService.triggerCallAlert(
          callID,
          `${callerName} - Cuộc gọi đến`,
          callerAvatar,
          'Đang đổ chuông...',
          true,
        );
      }
    } else {
      const newCall: CallInfo = {
        callId,
        type: extendedData.isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
        startTime: new Date(),
        inviter: invitation ? invitation.inviter : this.zimService.userId,
        extendedData,
      };

      this.callSessionStore.setCurrentCall(newCall);
      this.callSessionStore.setCurrentCallState(CALL_STATE.OUTGOING);
      this.callSessionStore.setParticipants(participants);
      this.callSessionStore.setPendingInvitation(null);
    }
  }

  toggleMic(): void {
    this.callMediaStore.toggleMic();
  }

  // Cleanup
  reset(preservePending = false): void {
    this.callSessionStore.reset(preservePending);
    this.callNetworkStore.resetNetworkQuality();
    this.isModalHidden.set(false);
    // Cuộc gọi kết thúc -> giải phóng ringtone
    this.callAudio.disposeAudio();
  }

  destroyCallZego() {
    this.callZegoListenerService.clearCallback();
    this.callZegoService.clearStreamCallbacks();
  }

  // Unlisten events when service is destroyed (if needed)
  destroy(): void {
    this.destroyCallZego();

    // Unlisten all events to prevent memory leaks
    this.callZimService.unlistenCallInvitationReceived();
    this.callZimService.unlistenCallInvitationCreated();
    this.callZimService.unlistenCallInvitationCancelled();
    this.callZimService.unlistenCallInvitationTimeout();
    this.callZimService.unlistenCallInvitationEnded();
    this.callZimService.unlistenCallUserStateChanged();
  }

  public showMissingCallNotification(event?: any) {
    const pending = this.pendingInvitation();
    console.log('DEBUG-CALL', 'showMissingCallNotification', pending);

    if (!pending) {
      if (event && event.callID) {
        this.localNotificationService.clearCallAlert(event.callID);
      }
      return;
    }

    const callID = pending.callId;
    const callerID = pending.inviter;
    const extendedData = pending.extendedData;
    const callerName = extendedData?.callerName || callID;
    const callerAvatar =
      this.callSessionStore.participants().find((el) => el.userId === callerID)?.avatar || '';

    this.localNotificationService.clearCallAlert(callID);

    if (!document.hidden) {
      return;
    }

    this.localNotificationService.triggerCallAlert(
      `${callID}_missing_call`,
      `${callerName} - Cuộc gọi ${pending.type === CALL_TYPE.AUDIO ? 'thoại' : 'video'} nhỡ`,
      callerAvatar,
      '',
    );
  }
}
